import { PLAYER_SIZE, ATTACK_RADIUS, MAX_ATTACK_RADIUS } from '../../constants';
import { WEAPON_ATTACK_PROFILES } from '../../data/items';
import WeaponSword    from '../../assets/WeaponSword';
import WeaponDagger   from '../../assets/WeaponDagger';
import WeaponTrident  from '../../assets/WeaponTrident';
import WeaponScythe   from '../../assets/WeaponScythe';
import WeaponUnarmed  from '../../assets/WeaponUnarmed';
import WeaponHammer   from '../../assets/WeaponHammer';
import WeaponStaff    from '../../assets/WeaponStaff';
import WeaponAxe      from '../../assets/WeaponAxe';
import WeaponKatana   from '../../assets/WeaponKatana';

const IS_TOUCH = navigator.maxTouchPoints > 0;

const ATTACK_WEAPON_MAP = {
  sword:   WeaponSword,
  dagger:  WeaponDagger,
  trident: WeaponTrident,
  scythe:  WeaponScythe,
  unarmed: WeaponUnarmed,
  hammer:  WeaponHammer,
  staff:   WeaponStaff,
  axe:     WeaponAxe,
  katana:  WeaponKatana,
};

/**
 * Per-weapon tight viewBox — bounds extracted from each weapon's path geometry.
 * aw/ah are the viewBox width and height in SVG units; weaponW is derived as
 * r × (aw/ah) so the SVG display matches the viewBox aspect ratio exactly and
 * the weapon fills the full attack-radius height without distortion.
 */
const WEAPON_VIEWBOXES = {
  sword:   { vb: '18 0 7 18',    aw:  7, ah: 18 },  // blade tip y≈0, pommel y≈16.5
  dagger:  { vb: '19 2 6 13',    aw:  6, ah: 13 },  // blade tip y≈4, pommel y≈14.5
  trident: { vb: '17 -4 12 28',  aw: 12, ah: 28 },  // prong tips y≈-3, butt y≈22.5
  scythe:  { vb: '5 -1 20 24',   aw: 20, ah: 24 },  // blade y≈0.5, pole butt y≈22
  unarmed: { vb: '16 10 10 10',  aw: 10, ah: 10 },  // gauntlet + spikes
  hammer:  { vb: '13 0 11 24',   aw: 11, ah: 24 },  // hammerhead y:0-7, pommel y≈23
  staff:   { vb: '18 -1 8 27',   aw:  8, ah: 27 },  // orb tip y≈-1, butt y≈24.5
  axe:     { vb: '12 0 12 25',   aw: 12, ah: 25 },  // blade sweep y:0-14, pommel y≈23.5
  katana:  { vb: '19 0 6 27',    aw:  6, ah: 27 },  // kissaki y≈0, kashira y≈26
};

/**
 * Renders the rotating attack sweep div + orbiting weapon SVG.
 * The weapon SVG now uses a per-weapon tight viewBox so its height fills the
 * full attack radius r — visually communicating the scaled attack range.
 *
 * Props: attackRenderKey, playerX, playerY, weaponType, rangeMult, cdMult, activeModifier
 */
export function AttackSweep({ attackRenderKey, playerX, playerY, weaponType, rangeMult, cdMult, activeModifier }) {
  if (!attackRenderKey) return null;

  const attackProfile = WEAPON_ATTACK_PROFILES[weaponType ?? 'unarmed'] ?? WEAPON_ATTACK_PROFILES.unarmed;
  const mobileMult = IS_TOUCH ? 0.5 : 1;
  const r      = Math.min(MAX_ATTACK_RADIUS, ATTACK_RADIUS * (rangeMult ?? 1) * attackProfile.radiusFactor * mobileMult);
  const isWide = attackProfile.shape === 'wide';
  const vw     = isWide ? r * 2.4 : r * 2;
  const vh     = isWide ? r * 1.2 : r * 2;
  const ax     = playerX + PLAYER_SIZE / 2 - vw / 2;
  const ay     = playerY + PLAYER_SIZE / 2 - vh / 2;

  const AttackWeapon = ATTACK_WEAPON_MAP[weaponType ?? 'unarmed'] ?? WeaponUnarmed;

  // Per-weapon tight viewBox: aspect ratio matches content so weapon fills height = r
  const wvb     = WEAPON_VIEWBOXES[weaponType ?? 'unarmed'] ?? WEAPON_VIEWBOXES.unarmed;
  const weaponH = r;
  const weaponW = Math.max(14, Math.round(r * wvb.aw / wvb.ah));
  const wx = vw / 2 - weaponW / 2;   // horizontally centred in sweep viewport
  const wy = vh / 2 - weaponH;       // handle at player centre, tip at centre − r

  return (
    <div
      key={attackRenderKey}
      className={`attack${attackProfile.cssClass ? ` ${attackProfile.cssClass}` : ''}`}
      style={{ left: ax, top: ay, width: vw, height: vh }}
    >
      <svg
        width={weaponW}
        height={weaponH}
        viewBox={wvb.vb}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          position: 'absolute',
          left:     wx,
          top:      wy,
          overflow: 'visible',
          filter:   'drop-shadow(0 0 6px rgba(255,220,80,0.95))',
        }}
      >
        <AttackWeapon />
      </svg>
    </div>
  );
}
